import { formatActivityDateTime } from "./driver-activity-format";
import { activityKindTheme } from "./driver-activity-theme";
import type { ActivityDriver } from "./driver-activity-view-model";

const HEADERS = [
  "Driver", "Company", "Type", "ID", "Pickup ticket", "Pickup time", "Pickup site", "Pickup tank", "Pickup qty",
  "Delivery ticket", "Delivery time", "Delivery site", "Delivery tank", "Delivery qty",
  "UOM", "Freight", "Work type", "Turnaround (min)", "Revenue", "Cost", "Validations", "Audit blocking", "Pictures", "Warnings",
];

const cell = (value: unknown) => {
  const text = value == null ? "" : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** The rows on screen as CSV, one line per entry, for a spreadsheet. */
export const driverActivityToCsv = (drivers: ActivityDriver[], timeZone?: string): string => {
  const time = (value?: number) => value == null ? "" : formatActivityDateTime(value, timeZone);
  const lines = drivers.flatMap((driver) => driver.shifts.flatMap((shift) => shift.entries.map((entry) => {
    const { pickup, delivery, tta } = entry;
    const other = entry.geofenceEvent ?? entry.serviceWork ?? entry.containerVolumeDrop;
    return [
      driver.name, driver.companyName, activityKindTheme[entry.kind].label, entry.resourceId,
      pickup?.ticketNumber, time(pickup ? tta?.pickupDttm ?? pickup.createDttm : other ? entry.time : undefined),
      pickup?.siteName ?? other?.siteName ?? entry.dispatch?.pickupSiteName, pickup?.containerName ?? entry.containerVolumeDrop?.containerName,
      pickup?.quantityDecimal ?? entry.containerVolumeDrop?.volume,
      delivery?.ticketNumber, time(delivery ? tta?.deliveryDttm ?? delivery.createDttm : other ? entry.endTime : undefined),
      delivery?.siteName ?? entry.dispatch?.deliverySiteName, delivery?.containerName, delivery?.quantityDecimal,
      pickup?.quantityUnitOfMeasure ?? delivery?.quantityUnitOfMeasure ?? entry.containerVolumeDrop?.unitOfMeasure,
      pickup?.waterTypeName ?? delivery?.waterTypeName, pickup?.serviceTypeName ?? delivery?.serviceTypeName ?? entry.serviceWork?.serviceTypeName,
      entry.durationMillis != null ? Math.round(entry.durationMillis / 60_000) : "",
      entry.revenue, entry.cost,
      entry.validation.filter((step) => step.validatedDttm != null).map((step) => step.label).join(" "),
      entry.audit?.hasAuditRun ? entry.audit.blockingFindingCount : "", entry.pictures.length || "",
      entry.warnings.map((warning) => warning.message).join(" | "),
    ].map(cell).join(",");
  })));
  return [HEADERS.join(","), ...lines].join("\n");
};

/** Hands the browser a CSV file to save. */
export const downloadDriverActivityCsv = (drivers: ActivityDriver[], fileName: string, timeZone?: string) => {
  const url = URL.createObjectURL(new Blob([driverActivityToCsv(drivers, timeZone)], { type: "text/csv;charset=utf-8" }));
  const link = Object.assign(document.createElement("a"), { href: url, download: fileName });
  link.click();
  URL.revokeObjectURL(url);
};
